/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ApolloQueryResult, FetchResult } from '@apollo/client';
import { ApolloClient, from, HttpLink, InMemoryCache } from '@apollo/client';
import { ApolloError } from '@apollo/client/errors';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import envConfig from '@hulu/env-config';
import { Auth } from 'aws-amplify';
import type { GraphQLFormattedError } from 'graphql';

import { onboardingActiveVar, RoutePath, selectedAdAccountIdVar, selectedOrganizationIdVar } from '../../constants';
import { localStorageGhostModeName } from '../../constants/ghostMode';
import { authorizationError } from '../../constants/global';
import { ERROR_TYPES, trackErrorOccurred } from '../../hooks/analytics';
import { syncSharedAuthCookies } from '../../utils/auth/sharedAuthCookies';
import { createRedirectSearchPath, needToRedirectToLogin } from '../../utils/routing/redirect';
import { User } from '../../utils/user';
import { HelpSiteCookieHelper } from '../../utils/user/interfaces';
import { operationName } from './adAccount/mutations/useUpdateLastActivity';
import result from './introspection';
import { operationOrgActivityName } from './organization/mutations/useUpdateOrgActivity/useUpdateOrgActivity';

export type GQLErrorDetail = {
  code: string;
  issue: string;
  field?: string;
  value?: string;
  location?: string;
  status: string;
};

export type GQLError = {
  details: GQLErrorDetail[];
  message: string;
};

const uri = envConfig?.REACT_APP_AD_MANAGER_API
  ? `${envConfig.REACT_APP_AD_MANAGER_API}/gql`
  : 'http://localhost:4000/gql';

const isAuthFeatureEnabled = /true/i.test(envConfig.REACT_APP_AUTH_FEATURE_ENABLED);

const PUBLIC_OPERATIONS = [
  'FeatureFlags',
  'resendInvite',
  'validateReCAPTCHA',
  'resendUserInviteWithReCAPTCHAValidation',
];

/**
 * This function is used to handle errors in a readable state to help make error
 * handling more generic so that the app can extract error messages from the server easier.
 * @param graphQLErrors ApolloError The error that came back from an Apollo Client call.
 */
export const handleGraphQLErrors = (graphQLErrors: ReadonlyArray<GraphQLFormattedError>): GQLError => {
  const errorResult: GQLError = {
    details: [],
    message: '',
  };

  // loop through all graphql errors
  graphQLErrors.forEach((gqlErr) => {
    const extensions: any = gqlErr.extensions || {};
    // we need to make sure there is something in there so we can get the response
    if (extensions && Object.keys(extensions).length && extensions.response) {
      const status = extensions.response?.status;
      if (status === 401) {
        // clear auth data
        console.warn('User is not authorized');

        selectedOrganizationIdVar(undefined);
        selectedAdAccountIdVar(undefined);
        onboardingActiveVar(undefined);

        if (needToRedirectToLogin(window.location.href)) {
          const isGhostMode = localStorage.getItem(localStorageGhostModeName);
          if (isGhostMode) {
            User.logout();
            window.location.href = `${window.location.origin}${RoutePath.ADMIN}`;
            return;
          }
          const searchPath = createRedirectSearchPath(window.location.pathname, window.location.search);
          if (extensions.response?.body?.error?.message) {
            sessionStorage.setItem(authorizationError, extensions.response.body.error.message);
          }
          window.location.href = `${window.location.origin}${RoutePath.LOGOUT}${searchPath}`;
        }

        return;
      }

      const details = extensions.response?.body?.error?.details;
      errorResult.message = extensions.response?.body?.error?.message;

      // for ach detail issue we want to capture that for showing errors from service
      if (details) {
        details.forEach((errorDetails: GQLErrorDetail) => errorResult.details.push(errorDetails));
      } else {
        const errorStatus: number | undefined = extensions.response.status || extensions.response.body.status;

        errorResult.details.push({
          code: '',
          issue: '',
          status: errorStatus ? String(errorStatus) : '404',
        });
      }
    }
  });

  return errorResult;
};

const operationsToRetryRequest: string[] = [operationName, operationOrgActivityName];

const MAX_RETRIES = 3;

const stringifyGraphqlErrorCode = (code: unknown): string | undefined => {
  if (code === null || code === undefined) {
    return undefined;
  }

  if (typeof code === 'string') {
    return code;
  }

  if (typeof code === 'number' || typeof code === 'boolean') {
    return String(code);
  }

  try {
    return JSON.stringify(code);
  } catch {
    return undefined;
  }
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    console.log(`[GraphQl error]: ${JSON.stringify(handleGraphQLErrors(graphQLErrors))}`);

    trackErrorOccurred({
      errorType: ERROR_TYPES.GRAPHQL,
      graphqlOperation: operation.operationName,
      errorCode: stringifyGraphqlErrorCode(graphQLErrors[0]?.extensions?.code),
    });

    if (operationsToRetryRequest.includes(operation.operationName)) {
      let retryCount = operation.getContext().retryCount ?? 0;

      if (retryCount < MAX_RETRIES) {
        retryCount++;
        operation.setContext({ retryCount });
        return forward(operation);
      }
    }
  }

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);

    trackErrorOccurred({
      errorType: ERROR_TYPES.NETWORK,
      graphqlOperation: operation.operationName,
    });
  }
});

const authLink = setContext(async ({ operationName }, { headers }) => {
  const isGhostMode = localStorage.getItem(localStorageGhostModeName);

  if (PUBLIC_OPERATIONS.includes(String(operationName))) {
    return {
      headers,
    };
  }

  if (isGhostMode) {
    await User.checkIdToken();
    const { idToken } = User.getUserInfo();

    return {
      headers: {
        ...headers,
        authorization: `Bearer ${idToken}`,
      },
    };
  }

  const session = await Auth.currentSession();

  await HelpSiteCookieHelper.setHelpSiteCookieForNonAdmin(true);
  syncSharedAuthCookies(
    {
      id_token: session.getIdToken().getJwtToken(),
      access_token: session.getAccessToken().getJwtToken(),
      refresh_token: session.getRefreshToken().getToken(),
    },
    'advertiser',
    selectedOrganizationIdVar() ?? undefined
  );

  if (session.isValid()) {
    const token = session.getIdToken().getJwtToken();

    return {
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
      },
    };
  } else {
    console.warn('User is not authorized');
    const searchPath = createRedirectSearchPath(window.location.pathname, window.location.search);
    window.location.href = `${window.location.origin}${RoutePath.LOGOUT}${searchPath}`;
  }
});

const httpLink = new HttpLink({ uri });
const removeTypenameLink = removeTypenameFromVariables();
let links = [errorLink, removeTypenameLink, httpLink];
if (isAuthFeatureEnabled) {
  links = [authLink].concat(links);
}

export const graphqlClient = new ApolloClient({
  cache: new InMemoryCache({
    possibleTypes: result.possibleTypes,
    typePolicies: {
      TemplateWithReport: {
        fields: {
          reports: {
            merge(_, incoming): any {
              return incoming;
            },
          },
        },
      },
    },
  }),
  link: from(links),
  connectToDevTools: false,
});

const { query, mutate } = graphqlClient;

// This is an apply method to be able to catch all errors when doing queries
graphqlClient.query = (...args): Promise<ApolloQueryResult<any>> => {
  return query.apply(graphqlClient, args).catch((error) => {
    throw new Error(JSON.stringify(handleGraphQLErrors(error.graphQLErrors)));
  });
};

// This is an apply method to be able to catch all errors when doing mutations
graphqlClient.mutate = (...args: any): Promise<FetchResult<any, Record<string, any>, Record<string, any>>> => {
  return mutate.apply(graphqlClient, args).catch((error: ApolloError) => {
    throw new Error(JSON.stringify(handleGraphQLErrors(error.graphQLErrors)));
  });
};
